import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiMapPin } = FiIcons;

const SanDiego = () => {
  const services = [
    'General Liability Insurance',
    'Professional Liability Insurance', 
    'Business Owners Policy (BOP)',
    'Workers Compensation',
    'Cyber Insurance',
    'Commercial Property Insurance'
  ];

  const neighborhoods = [
    'Downtown San Diego', 'La Jolla', 'Mission Valley', 'Hillcrest', 'North Park',
    'Pacific Beach', 'Mission Beach', 'Gaslamp Quarter', 'Little Italy', 'Balboa Park',
    'Coronado', 'Chula Vista', 'Carlsbad', 'Encinitas', 'Del Mar'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-900 to-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Small Business Insurance San Diego
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto"
            >
              Comprehensive business insurance solutions for San Diego companies. Licensed California insurance agency serving America's Finest City with fast, affordable coverage through Coterie Insurance.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get San Diego Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Expertise Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                San Diego Business Insurance Experts
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                As a licensed California insurance agency (CA License #6013543), Burbank Insurance Services understands the unique challenges facing San Diego businesses. From biotech companies in Torrey Pines to hospitality businesses in the Gaslamp Quarter, we provide tailored insurance solutions.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                San Diego's diverse economy and growing business community requires specialized insurance expertise. Our partnership with Coterie Insurance ensures you get comprehensive protection designed for Southern California businesses.
              </p>
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiMapPin} className="h-6 w-6 text-teal-600" />
                <span className="text-gray-800 font-medium">Serving all San Diego County</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us for San Diego Business Insurance?</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-teal-600" />
                  <span className="text-gray-800">Licensed California insurance professionals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-teal-600" />
                  <span className="text-gray-800">Biotech & life sciences expertise</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-teal-600" />
                  <span className="text-gray-800">Tourism & hospitality coverage</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-teal-600" />
                  <span className="text-gray-800">Same-day coverage available</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Business Insurance Services in San Diego
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coverage options for San Diego businesses across all industries.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow"
              >
                <SafeIcon icon={FiShield} className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{service}</h3>
                <p className="text-gray-600">Professional coverage tailored for San Diego businesses.</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served Section */}
      <section className="py-20 bg-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              San Diego Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Providing business insurance throughout San Diego County and surrounding areas.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {neighborhoods.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-4 rounded-lg text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="text-gray-800 font-medium">{area}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your San Diego Business?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Join thousands of San Diego businesses that trust Burbank Insurance Services for their commercial insurance needs.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get Your San Diego Business Quote
            </a>
          </div>
          <p className="text-teal-200 text-sm mt-4">
            CA License #6013543 • Serving San Diego County • Same-day coverage available
          </p>
        </div>
      </section>
    </div>
  );
};

export default SanDiego;